import { safeParseInt } from 'shared/util'
import type {
  AxisType,
  AxisValue,
  CategoryType,
  DashBoardCondition,
  DashBoardConditions,
  DashBoardConfigure,
  DashBoardItemOption,
  ValueType,
} from '../types'

import { TargetListResult } from "interface/search/targetList"
import { KeyOfTargetListCategory, KeyOfTargetListScore } from 'features/search/targetList/util'

export const initDashBoardConditions = <T>(
  configure: DashBoardConfigure<T>,
  params: URLSearchParams
) => {
  const conditions: DashBoardConditions = {}
  for (const axis in configure.axis) {
    let text = params.get('c_' + axis)
    if (text) {
      let not = false
      let over = false
      let under = false
      if (text.startsWith('!')) {
        not = true
        if (text.startsWith('!>')) {
          over = true
          text = text.substring(2)
        } else if (text.startsWith('!<')) {
          under = true
          text = text.substring(2)
        } else {
          text = text.substring(1)
        }
      } else if (text.startsWith('>')) {
        over = true
        text = text.substring(1)
      } else if (text.startsWith('<')) {
        under = true
        text = text.substring(1)
      }
      let codes: string[] = []
      let label = ''
      const sep = text.indexOf('@')
      if (sep >= 0) {
        codes = text.substring(0, sep).split(',')
        label = text.substring(sep + 1)
      } else {
        codes = text.split(',')
      }
      conditions[axis] = {
        code: codes,
        label: label,
        not: not ? true : undefined,
        over: over ? true : undefined,
        under: under ? true : undefined
      }
    }
  }
  return conditions
}
export const applyDashBoardCondition = <T>(
  configure: DashBoardConfigure<T>,
  conditions: DashBoardConditions,
  params: URLSearchParams
) => {
  for (const axis in configure.axis) {
    const key = 'c_' + axis
    const condition = conditions[axis]
    if (condition) {
      const text = (
        condition.not ? (
          condition.over ? '!>' :
          condition.under ? '!<' : '!'
        ) : (
          condition.over ? '>' :
          condition.under ? '<' : ''
        )
      ) + condition.code.join(',') + '@' + condition.label
      params.set(key, text)
    } else if (params.get(key)) {
      params.delete(key)
    }
  }
}

const timeCompare = (
  conditions: string[],
  time: Date
) => {
  return conditions.some((it) => it.split(',').every((timeCondition) => {
    const separator = timeCondition.indexOf(':')
    if (separator < 0) return false
    const left = timeCondition.substring(0, separator)
    const right = safeParseInt(timeCondition.substring(separator + 1))
    if (left === 'year') return time.getFullYear() === right
    if (left === 'month') return time.getMonth() === right
    if (left === 'date') return time.getDate() === right
    if (left === 'wday') return time.getDay() === right
    if (left === 'hour') return time.getHours() === right
    if (left === 'hour3') return Math.floor(time.getHours() / 3) === right
    return false
  }))
}

export const filterCompare = (
  condition: DashBoardCondition,
  value: AxisValue
): boolean => {
  if (!value?.code) return false
  if (condition.not) {
    if (condition.over) {
      return !condition.code.some((it) => value.code.localeCompare(it) >= 0)
    } else if (condition.under) {
      return !condition.code.some((it) => value.code.localeCompare(it) <= 0)
    } else {
      return !condition.code.some((it) => value.code === it)
    }
  } else {
    if (condition.over) {
      return condition.code.some((it) => value.code.localeCompare(it) >= 0)
    } else if (condition.under) {
      return condition.code.some((it) => value.code.localeCompare(it) <= 0)
    } else {
      return condition.code.some((it) => value.code === it)
    }
  }
}
export const filter = <T>(
  data: T,
  configure: DashBoardConfigure<T>,
  conditions: DashBoardConditions,
  excludeKey?: string,
  targetList?: TargetListResult
) => {
  return !Object.keys(conditions).some((axisKey) => {
    if (axisKey === excludeKey) return false
    if (axisKey === "identifier") {
      if (!configure.identifier) return false
      const identifier = configure.identifier(data)
      if (!identifier) return false
      const condition = conditions[axisKey]
      if (condition.code.indexOf(identifier.id) < 0) return true
      return false
    } else {
      const def = getAxisDefByKey(configure, targetList, axisKey)
      if (!def) return true
      const condition = conditions[axisKey]
      if (condition.time) {
        if (!def.time) return true
        const time = def.time(data)
        if (!time) return true
        if (condition.not) {
          return timeCompare(condition.code, time)
        } else {
          return !timeCompare(condition.code, time)
        }
      } else {
        const value = def.val(data)
        if (!value) return true
        if (Array.isArray(value)) {
          if (condition.not) {
            return value.some((v) => !filterCompare(condition, v))
          } else {
            return !value.some((v) => filterCompare(condition, v))
          }
        } else {
          return !filterCompare(condition, value)
        }
      }
    }
  })
}
const getAxisDefByKey = <T,>(
  configure: DashBoardConfigure<T>,
  targetList: TargetListResult | undefined,
  key: string
): AxisType<T> | undefined => {
  if (key.startsWith(KeyOfTargetListCategory)) {
    if (!targetList?.category) return undefined
    const valueFunc = configure.targetList?.category
    if (!valueFunc) return undefined
    const sequence = parseInt(key.substring(KeyOfTargetListCategory.length))
    const def = targetList.category.find((category) => category.sequence === sequence)
    return def ? {
      title: def.title,
      val: (data) => valueFunc(data, sequence)
    } : undefined
  }
  return configure.axis[key]
}

export const getAxisDef = <T,>(
  configure: DashBoardConfigure<T>,
  options: DashBoardItemOption[] | undefined,
  id: string,
  globalKey?: string,
  targetList?: TargetListResult
): AxisType<T> | undefined => {
  let key = options?.find((opt) => opt.id === id)?.value
  if (!key) return undefined
  if (key === "@global") {
    if (!globalKey) return undefined
    key = globalKey
  }
  const def = getAxisDefByKey(configure, targetList, key)
  return def ? { ...def, key } : undefined
}

export const getValueDef = <T,>(
  configure: DashBoardConfigure<T>,
  options: DashBoardItemOption[] | undefined,
  id: string,
  targetList?: TargetListResult
): ValueType<T> | undefined => {
  const key = options?.find((opt) => opt.id === id)?.value ?? 'count'
  if (key.startsWith(KeyOfTargetListScore)) {
    if (!targetList?.score) return undefined
    const valueFunc = configure.targetList?.score
    if (!valueFunc) return undefined
    const sequence = parseInt(key.substring(KeyOfTargetListScore.length))
    const def = targetList.score.find((score) => score.sequence === sequence)
    return def ? {
      title: def.title,
      val: (data) => valueFunc(data, sequence),
      key
    } : undefined
  } else {
    const def = key ? configure.value[key] : undefined
    return def ? { ...def, key } : undefined
  }
}

export const getKeyColumns = (
  defs: { [index: string]: {
    readonly title: string
    readonly category?: string
    readonly key?: string
  } },
  categories?: { [index: string]: CategoryType }
) => {
  const categoryList: {
    category: string
    title: string
    column: number
    keys: string[]
  }[] = []
  const emptyKeys: string[] = []
  for (const key in defs) {
    const def = defs[key]
    if (categories && def.category) {
      const category = categories[def.category]
      if (category) {
        let it = categoryList.find((category) => category.category === def.category)
        if (!it) {
          it = {
            category: def.category,
            title: category.title,
            column: category.column,
            keys: []
          }
          categoryList.push(it)
        }
        it.keys.push(key)
        continue
      }  
    }
    emptyKeys.push(key)
  }
  if (emptyKeys.length > 0) {
    categoryList.unshift({
      category: '',
      title: '',
      column: 0,
      keys: emptyKeys
    })
  }
  const columnList: {
    column: number
    category: {
      title: string
      keys: string[]
    }[]
  }[] = []
  for (const category of categoryList) {
    let column = columnList.find((column) => column.column === category.column)
    if (!column) {
      column = {
        column: category.column,
        category: []
      }
      columnList.push(column)
    }
    column.category.push({
      title: category.title,
      keys: category.keys
    })
  }
  return columnList
}
